import * as React from 'react'

import {
  Box,
  CheckBox,
  Table,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Selector from 'modules/core/components/Selector'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import { employeeJobTitle } from 'modules/contracts/lib/utils'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import useAssignableJobRoles from 'modules/job_catalog/hooks/useAssignableJobRoles'
import { getLevelWarning } from 'modules/job_catalog/lib/getLevelWarning'
import { getRoleWarning } from 'modules/job_catalog/lib/getRoleWarning'
import jobLevelsCollection, {
  jobRoleHasAssignableLevels,
  jobRoleLevels,
} from 'modules/job_catalog/store/collections/levels'
import { jobLevelRole } from 'modules/job_catalog/store/collections/roles'
import type JobLevel from 'modules/job_catalog/store/models/JobLevel'
import type JobRole from 'modules/job_catalog/store/models/JobRole'

export enum Status {
  OpenToChanges,
  Changed,
  Loading,
  Success,
  ServerError,
  InvalidLevel,
}

interface Props {
  rowKey: string
  employee: Employee
  status: Status
  role: JobRole | undefined
  level: JobLevel | undefined
  selected: boolean
  onToggleSelection: (key: string, isSelected: boolean) => void
  onRoleChange: (key: string, role: JobRole | undefined) => void
  onLevelChange: (key: string, level: JobLevel | undefined) => void
  onSelectionFinished: (key: string) => void
}

const Row: React.FC<Props> = ({
  rowKey,
  employee,
  role,
  level,
  status,
  selected,
  onToggleSelection,
  onRoleChange,
  onLevelChange,
  onSelectionFinished,
}) => {
  const [showJobTitleTooltip, toggleJobTitleTooltip] = useToggle(false)
  const [levelSelectorOpened, setLevelSelectorOpened] = React.useState(false)

  const roleId = role?.attributes.id
  const legalEntity = employeeLegalEntity(employee)
  const { assignableJobRoles, isJobRoleAssignable } = useAssignableJobRoles([legalEntity.id], role)

  const levelId = level?.attributes?.id
  const activeLevels = role ? jobRoleLevels(role) : []
  const levels = !level || level?.isActive ? activeLevels : [level, ...activeLevels]

  const isStatusChanged = status == Status.Changed
  const isStatusLoading = status == Status.Loading
  const isStatusServerError = status == Status.ServerError
  const isStatusSuccess = status == Status.Success
  const isStatusInvalidLevel = status == Status.InvalidLevel

  const roleError =
    !levelSelectorOpened && isStatusInvalidLevel ? i18n.t('job_catalog.select_a_level_error') : ''
  const serverErrorMessage = isStatusServerError ? i18n.t('errors.generic_error') : ''
  const roleWarning = serverErrorMessage || getRoleWarning(role, legalEntity)?.warning || ''
  const levelWarning = serverErrorMessage || getLevelWarning(level)?.warning || ''

  const handleRoleSelect = (id: number | null) => {
    const nextRole = id ? assignableJobRoles.find((x) => x.attributes.id === id) : undefined

    if (nextRole && jobRoleHasAssignableLevels(nextRole)) setLevelSelectorOpened(true)
    onRoleChange(rowKey, nextRole)
  }

  const handleLevelSelect = (id: number | null) => {
    const nextLevel = id ? jobLevelsCollection.get(id) : undefined

    setLevelSelectorOpened(false)
    onLevelChange(rowKey, nextLevel)
  }

  const handleSelectedChange = () => {
    onToggleSelection(rowKey, !selected)
  }

  const handleLevelSelectorToggle = () => {
    setLevelSelectorOpened(!levelSelectorOpened)
    if (levelSelectorOpened) onSelectionFinished(rowKey)
  }

  const rolesOptions = useUnsafeMemo(() => {
    return assignableJobRoles.map((x) => {
      const { id, name } = x.attributes
      return (
        <Selector.Option
          key={id}
          value={id}
          label={name}
          searchValue={name}
          isHidden={!isJobRoleAssignable(x)}
        >
          {name}
        </Selector.Option>
      )
    })
  }, [assignableJobRoles, isJobRoleAssignable])

  const levelsOptions = levels.map((level) => {
    const { id, name } = level.attributes

    return (
      <Selector.Option
        key={id}
        value={id}
        label={name}
        searchValue={name}
        isHidden={level.isArchived}
      >
        {name}
      </Selector.Option>
    )
  })

  const jobRole = level ? jobLevelRole(level) : null

  return (
    <Table.Row>
      <Table.Cell colSpan={2}>
        <Box
          flexDirection="row"
          justifyContent="flexStart"
          alignItems="center"
          gap="s12"
          padding="s12"
        >
          <CheckBox checked={selected} onClick={handleSelectedChange} />
          <UserAvatar size="s32" user={employeeUser(employee)} />
          <Text.Normal>{employeeUser(employee).fullName()}</Text.Normal>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box paddingX="s16" paddingY="s12" alignItems="flexStart">
          <Tooltip.Alt
            attachment="bottom center"
            targetAttachment="top center"
            toggle={toggleJobTitleTooltip}
            open={
              (employeeJobTitle(contractVersions, employee)?.length || 0) > 25
                ? showJobTitleTooltip
                : false
            }
            label={employeeJobTitle(contractVersions, employee)}
          >
            <Text.Normal>
              {employeeJobTitle(contractVersions, employee) || i18n.t('common.not_applicable')}
            </Text.Normal>
          </Tooltip.Alt>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={3}>
        <Box flexDirection="row" gap="s24" padding="s12" justifyContent="spaceEvenly">
          <Selector.Popover
            headerLabel={i18n.t('common.name')}
            blankLabel={i18n.t('common.empty')}
            value={roleId ? [roleId] : []}
            onChange={(id) => handleRoleSelect(id as number | null)}
            placeholder={i18n.t('job_catalog.choose_role')}
            searchPlaceholder={i18n.t('common.search')}
            changed={isStatusChanged}
            loading={isStatusLoading}
            completed={isStatusSuccess}
            warning={roleWarning}
            error={roleError}
          >
            {rolesOptions || []}
          </Selector.Popover>

          <Selector.Popover
            headerLabel={i18n.t('common.name')}
            blankLabel={i18n.t('common.empty')}
            isDisabled={!role || !jobRoleLevels(role).length}
            value={level?.isDefault || !levelId ? [] : [levelId]}
            onChange={(id) => handleLevelSelect(id as number | null)}
            placeholder={
              level && level.isDefault && jobRole && !jobRoleHasAssignableLevels(jobRole)
                ? i18n.t('job_catalog.level_not_available')
                : i18n.t('job_catalog.choose_level')
            }
            searchPlaceholder={i18n.t('common.search')}
            changed={isStatusChanged}
            loading={isStatusLoading}
            completed={isStatusSuccess}
            warning={levelWarning}
            isOpen={levelSelectorOpened}
            onToggle={handleLevelSelectorToggle}
          >
            {levelsOptions || []}
          </Selector.Popover>
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default observer(Row)
